import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ScanSearch, Trash2, WandSparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PhotoHoldCanvas } from '../../../components/climbs/photo-editor/PhotoHoldCanvas';
import { usePhotoHolds } from '../../../components/climbs/photo-editor/usePhotoHolds';
import { DockedBar } from '../../../components/climbs/climb-editor/DockedBar';
import { DRAWING_SURFACE_ATTRIBUTE } from '../../../components/climbs/climb-editor/useClearSelectionOutside';
import type { Hold } from '../../../types/climb';
import type { PhotoArea } from '../../../types/climb-scan';
import { ScanColorPreviews, type ScanColorPreview } from './ScanColorPreviews';
import { ScanEditorLayout, type ScanEditorChrome } from './ScanEditorLayout';

interface ScanPlaceholderPickerProps {
  imageUrl: string;
  naturalWidth: number;
  naturalHeight: number;
  /** Every hold the scan found, as marks. Fixed: nothing here changes them. */
  placeholders: Hold[];
  /**
   * Which of them the detector was unsure of, by place: drawn as maybes,
   * fainter and dotted, until one is taken.
   */
  maybe?: number[];
  /**
   * Make a climb of the holds that are lit. Absent on a wall that makes no
   * climbs here — a board's photo, whose lit holds are only ever excluded.
   */
  onAdd?: (holds: Hold[]) => void;
  adding?: boolean;
  /**
   * The climbs already made off this wall, drawn on it and pressable — so the
   * photo is a way into the list and not only a view of it.
   */
  previews?: ScanColorPreview[];
  onSelectColor?: (colorId: string) => void;
  /**
   * The scans page's columns. Absent, the photo is all that is rendered, for
   * a page that lays its own columns out (the board panel).
   */
  chrome?: ScanEditorChrome;
  testId?: string;
  /**
   * Looking again at an area of the wall for holds the scan missed. Armed from
   * the controls column (ScanWallTools): while `drawing`, the next drag draws
   * a rectangle and `onScan` is handed it. While `running`, `area` is the one
   * being looked at, drawn on the wall. Absent where the detector cannot look
   * at an area.
   */
  areaScan?: {
    running: boolean;
    area?: PhotoArea;
    drawing: boolean;
    onDrawingChange: (drawing: boolean) => void;
    onScan: (area: PhotoArea) => void;
  };
  /**
   * Told which placeholders are lit, by place, whenever that changes: the
   * controls column can act on them too (excluding them from the wall).
   */
  onChosenChange?: (indices: number[]) => void;
  /**
   * The colour-wand, armed from the controls column (ScanWallTools): a press
   * on the photo is a point (`onPick`) rather than a hold, and the patch grown
   * from it is drawn over the wall (`outline`, `point`). `onCancel` puts it
   * down from the photo's bar.
   */
  wand?: {
    outline: Array<[number, number]> | null;
    point: { x: number; y: number } | null;
    onPick: (point: { x: number; y: number }) => void;
    onCancel: () => void;
  };
}

/**
 * The wall as the scan left it: every hold on it, waiting to be made into
 * climbs.
 *
 * **This is what the scans page shows before any climb is open**, and it is the
 * state the whole phase was for. The photograph is drained of its colour and
 * each hold the detector found is drawn on it as a placeholder; pressing one
 * lights it up. A climb is then the handful that are lit — which is a different
 * act from drawing one, and a much shorter one.
 *
 * **Nothing here is drawn, moved or resized.** The holds are measured from the
 * pixels of the wall, so the only thing left to say is which of them this climb
 * is. The gestures follow from that: a press takes one, ⌘-press takes another
 * as well, and a drag sweeps up everything it crosses — which is the gesture
 * that can afford to replace drawing, because there is nothing to draw.
 *
 * The holds themselves belong to the scan and outlive any climb made from them:
 * taking a hold out of a climb puts it back here rather than destroying it.
 */
export function ScanPlaceholderPicker({
  imageUrl,
  naturalWidth,
  naturalHeight,
  placeholders,
  maybe,
  onAdd,
  adding,
  previews,
  onSelectColor,
  chrome,
  areaScan,
  onChosenChange,
  wand,
  testId = 'scan-placeholder-frame',
}: ScanPlaceholderPickerProps) {
  const { t } = useTranslation();

  /**
   * Whether the next drag draws an area to look at again rather than
   * gathering holds. Off after one rectangle: it is an occasional act, and a
   * mode left on would turn the wall's main gesture into something else.
   */
  const drawingArea = !!areaScan?.drawing && !areaScan.running;

  /**
   * The holds that are **in the climb being built**, by their place in the
   * pool.
   *
   * Held apart from the editor's own selection, because on a wall where every
   * hold is already drawn the two questions come apart: a plain press says *is
   * this hold part of the climb*, and ⌘-press says *which of the climb's holds
   * am I about to change*. One is membership and the other is a grip.
   */
  const [active, setActive] = useState<number[]>([]);

  /**
   * **The pool changes under the climb when the slider moves**, and `active`
   * is places in it: a hold hidden before them would shift every one after it
   * onto its neighbour. So they are carried across by where the hold sits,
   * during the render that brings the new pool rather than in an effect, so
   * the wrong holds are never lit even for a frame. A lit hold the slider
   * hides drops out of the climb.
   */
  const [pool, setPool] = useState(placeholders);
  if (pool !== placeholders) {
    setPool(placeholders);
    setActive(
      active.flatMap((index) => {
        const hold = pool[index];
        const at = hold ? placeholders.findIndex((other) => other.x === hold.x && other.y === hold.y) : -1;
        return at >= 0 ? [at] : [];
      })
    );
  }

  const editor = usePhotoHolds({
    naturalWidth,
    naturalHeight,
    holds: placeholders,
    // The wall is not edited here. Every gesture that would change it is off,
    // so there is nothing for a change handler to be told about.
    onChange: () => {},
    drawing: false,
    marquee: true,
    activation: { active, onChange: setActive },
    onSweepArea:
      drawingArea && areaScan
        ? (area) => {
            areaScan.onDrawingChange(false);
            areaScan.onScan(area);
          }
        : undefined,
  });

  // The grip is places too, and only ever a moment's: let go of it when the
  // pool changes, and when the next drag is about to draw an area instead.
  const { clearSelection } = editor;
  useEffect(() => clearSelection(), [placeholders, drawingArea, clearSelection]);

  const chosen = active.map((index) => placeholders[index]).filter(Boolean);

  useEffect(() => onChosenChange?.(active), [active, onChosenChange]);

  /** Take the holds in hand back out of the climb, leaving them on the wall. */
  function removeSelected() {
    setActive(active.filter((index) => !editor.selected.includes(index)));
    editor.clearSelection();
  }

  const canvas = (
    <div className="relative" {...{ [DRAWING_SURFACE_ATTRIBUTE]: '' }}>
      <PhotoHoldCanvas
        editor={editor}
        imageUrl={imageUrl}
        testId={testId}
        className="rounded-md border"
        holdLabel={({ x, y }) => t('gymAdmin.scans.placeholderAt', { x, y })}
        resetZoomLabel={t('gymAdmin.scans.resetZoom')}
        spotlight
        // Only what is in the climb is lit; the rest of the wall stays grey
        // and dashed, which is what says it is there to be taken.
        lit={active}
        maybe={maybe}
        pendingArea={areaScan?.running ? areaScan.area : undefined}
        onPickPoint={wand?.onPick}
        pickPreview={wand ? { outline: wand.outline, point: wand.point } : undefined}
      />

      {previews && onSelectColor && <ScanColorPreviews previews={previews} onSelect={onSelectColor} />}

      <DockedBar>
        {wand ? (
          <div className="flex items-center gap-2 rounded-full border border-border bg-background/95 p-1 pl-3 shadow-xl backdrop-blur">
            <WandSparkles className="size-4 text-muted-foreground" aria-hidden />
            <span className="text-sm text-muted-foreground">{t('gymAdmin.scans.wand.hint')}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 rounded-full"
              aria-label={t('gymAdmin.scans.wand.done')}
              onClick={wand.onCancel}
            >
              <X className="size-4" aria-hidden />
            </Button>
          </div>
        ) : drawingArea ? (
          <div className="flex items-center gap-2 rounded-full border border-border bg-background/95 p-1 pl-3 shadow-xl backdrop-blur">
            <ScanSearch className="size-4 text-muted-foreground" aria-hidden />
            <span className="text-sm text-muted-foreground">{t('gymAdmin.scans.areaScan.hint')}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 rounded-full"
              aria-label={t('gymAdmin.scans.areaScan.cancel')}
              onClick={() => areaScan?.onDrawingChange(false)}
            >
              <X className="size-4" aria-hidden />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-full border border-border bg-background/95 p-1 pl-3 shadow-xl backdrop-blur">
            <span className="text-sm tabular-nums text-muted-foreground">
              {t('gymAdmin.scans.chosenHolds', { count: chosen.length })}
            </span>
            {/* What ⌘-press is for: a handful of the climb's holds in hand, to
                take back out together. */}
            {editor.selected.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 rounded-full text-destructive hover:text-destructive"
                aria-label={t('gymAdmin.scans.removeChosen', { count: editor.selected.length })}
                onClick={removeSelected}
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            )}
            {chosen.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 rounded-full"
                aria-label={t('gymAdmin.scans.clearChosen')}
                onClick={() => {
                  setActive([]);
                  editor.clearSelection();
                }}
              >
                <X className="size-4" aria-hidden />
              </Button>
            )}
            {onAdd && (
              <Button
                type="button"
                size="sm"
                className="rounded-full"
                disabled={chosen.length === 0 || adding}
                onClick={() => onAdd(chosen)}
              >
                <Plus className="mr-1 size-4" aria-hidden />
                {t('gymAdmin.scans.addColor')}
              </Button>
            )}
          </div>
        )}
      </DockedBar>
    </div>
  );

  return chrome ? <ScanEditorLayout canvas={canvas} {...chrome} /> : canvas;
}

